import { ChevronLeftIcon, ChevronRightIcon } from '@velocity/icons/system'
import { Button } from '@velocity/ui'
import * as React from 'react'

import { useContent } from '@ngb-frontend/shared/context'

import { useStepFooterNextDisabled } from './StepFooterContext'
import { useStepNavigationFooterStyles } from './StepNavigationFooter.styled'

/**
 * Shared `<form>` id for driver journey steps. Each driver step form sets this
 * id on its `<form>` so the sticky footer's "Next" button can submit it from
 * outside the form (native `form` attribute). Only one driver step form is
 * mounted at a time, so the id stays unique.
 */
export const DRIVER_STEP_FORM_ID = 'driver-step-form'

interface StepNavigationFooterProps {
  /** Show the "Previous" button (defaults to true) */
  showPrevious?: boolean
  /** Show the "Next" button (defaults to true) */
  showNext?: boolean
  /** Content key for the "Next" label (defaults to `steps.shared.next`) */
  nextLabel?: string
  /** Variables of the "Next" label */
  nextLabelVariables?: Record<string, string>
  /** Content key for the "Previous" label (defaults to `steps.shared.previous`) */
  previousLabel?: string
  /** Called when "Previous" is clicked (wired to the layout step-back handler) */
  onPrevious?: () => void
  /**
   * Optional click handler for "Next". When provided the button is a plain
   * button (e.g. result pages); when omitted "Next" submits the form `formId`.
   */
  onNext?: () => void
  /** Disable the "Next" button */
  nextDisabled?: boolean
  /** Id of the `<form>` the "Next" button submits (defaults to the driver step form) */
  formId?: string
}

export const StepNavigationFooter: React.FC<StepNavigationFooterProps> = ({
  showPrevious = true,
  showNext = true,
  nextLabel,
  nextLabelVariables,
  previousLabel,
  onPrevious,
  onNext,
  nextDisabled,
  formId = DRIVER_STEP_FORM_ID,
}) => {
  const c = useContent()
  const classes = useStepNavigationFooterStyles()
  // Steps that gate their primary action push a disabled flag up via context.
  const contextNextDisabled = useStepFooterNextDisabled()

  // Auto-advance steps hide both buttons; skip the bar entirely so they don't
  // get an empty sticky strip with a top border.
  if (!showPrevious && !showNext) return null

  return (
    <div className={classes.root} data-e2e-component="step-navigation-footer">
      {showPrevious && (
        <Button
          variant="secondary-outlined"
          type="button"
          StartIcon={ChevronLeftIcon}
          onClick={onPrevious}
          data-e2e-component="previous-button"
        >
          {c(previousLabel || 'steps.shared.previous')}
        </Button>
      )}
      {showNext && (
        <Button
          className={!showPrevious ? classes.spacer : undefined}
          variant="primary-filled"
          type={onNext ? 'button' : 'submit'}
          form={onNext ? undefined : formId}
          onClick={onNext}
          EndIcon={ChevronRightIcon}
          disabled={nextDisabled || contextNextDisabled}
          data-e2e-component="next-button"
        >
          {c(nextLabel || 'steps.shared.next', {
            variables: nextLabelVariables,
          })}
        </Button>
      )}
    </div>
  )
}
